import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

// ── Types ───────────────────────────────────────────────────────────────

export type POStatus = "draft" | "submitted" | "sent" | "partial" | "complete" | "cancelled";

export interface PurchaseOrder {
  id: string;
  org_id: string;
  po_number: string;
  vendor_id: string | null;
  status: POStatus;
  order_date: string | null;
  expected_date: string | null;
  received_date: string | null;
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  notes: string | null;
  ship_to: string | null;
  bill_to: string | null;
  created_by: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface POLineItem {
  id: string;
  org_id: string;
  po_id: string;
  part_id: string | null;
  description: string;
  quantity: number;
  unit_cost: number;
  line_total: number;
  qty_received: number;
  created_at: string;
  updated_at: string;
}

export interface CreatePOInput {
  vendor_id?: string | null;
  order_date?: string;
  expected_date?: string;
  tax?: number;
  shipping?: number;
  notes?: string;
}

export interface UpdatePOInput {
  vendor_id?: string | null;
  status?: POStatus;
  order_date?: string | null;
  expected_date?: string | null;
  received_date?: string | null;
  tax?: number;
  shipping?: number;
  notes?: string | null;
  ship_to?: string | null;
  bill_to?: string | null;
}

export interface CreatePOLineInput {
  po_id: string;
  part_id?: string | null;
  description: string;
  quantity: number;
  unit_cost: number;
}

export interface UpdatePOLineInput {
  part_id?: string | null;
  description?: string;
  quantity?: number;
  unit_cost?: number;
  qty_received?: number;
}

// ── Queries ─────────────────────────────────────────────────────────────

export function usePurchaseOrders(filters?: {
  status?: POStatus;
  vendor_id?: string;
  search?: string;
}) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["purchase-orders", filters],
    queryFn: async () => {
      let query = supabase
        .from("purchase_order" as any)
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (filters?.status) query = query.eq("status", filters.status);
      if (filters?.vendor_id) query = query.eq("vendor_id", filters.vendor_id);
      if (filters?.search) query = query.ilike("po_number", `%${filters.search}%`);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as PurchaseOrder[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}

export function usePurchaseOrder(id: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["purchase-orders", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("purchase_order" as any)
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as PurchaseOrder;
    },
    enabled: !!id && isAuthenticated && !authLoading,
  });
}

export function usePOLineItems(poId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["po-line-items", poId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("po_line_item" as any)
        .select("*")
        .eq("po_id", poId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as POLineItem[];
    },
    enabled: !!poId && isAuthenticated && !authLoading,
  });
}

// ── Mutations ───────────────────────────────────────────────────────────

export function useCreatePO() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePOInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id, id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      // Generate PO number via RPC
      const { data: poNumber, error: numError } = await supabase
        .rpc("generate_po_number", { p_org_id: userData.org_id });
      if (numError) throw numError;

      const { data, error } = await supabase
        .from("purchase_order" as any)
        .insert([{
          org_id: userData.org_id,
          po_number: poNumber,
          vendor_id: input.vendor_id || null,
          order_date: input.order_date || null,
          expected_date: input.expected_date || null,
          tax: input.tax || 0,
          shipping: input.shipping || 0,
          notes: input.notes || null,
          created_by: userData.id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as PurchaseOrder;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      toast({ title: "PO created", description: `Purchase order ${data.po_number} has been created.` });
    },
    onError: (error: Error) => {
      toast({ title: "Error creating PO", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdatePO() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdatePOInput }) => {
      const { data, error } = await supabase
        .from("purchase_order" as any)
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as PurchaseOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      toast({ title: "PO updated", description: "The purchase order has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error updating PO", description: error.message, variant: "destructive" });
    },
  });
}

export function useCreatePOLine() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePOLineInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("po_line_item" as any)
        .insert([{
          org_id: userData.org_id,
          po_id: input.po_id,
          part_id: input.part_id || null,
          description: input.description,
          quantity: input.quantity,
          unit_cost: input.unit_cost,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as POLineItem;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["po-line-items", data.po_id] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      toast({ title: "Line added", description: "Line item has been added to the PO." });
    },
    onError: (error: Error) => {
      toast({ title: "Error adding line", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdatePOLine() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, poId, input }: { id: string; poId: string; input: UpdatePOLineInput }) => {
      const { data, error } = await supabase
        .from("po_line_item" as any)
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return { ...data, poId } as POLineItem & { poId: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["po-line-items", (data as any).poId] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      toast({ title: "Line updated", description: "Line item has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error updating line", description: error.message, variant: "destructive" });
    },
  });
}

export function useDeletePO() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("purchase_order" as any)
        .update({ is_active: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      toast({ title: "Purchase order deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Error deleting PO", description: error.message, variant: "destructive" });
    },
  });
}

export function useDeletePOLine() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, poId }: { id: string; poId: string }) => {
      const { error } = await supabase
        .from("po_line_item" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
      return { poId };
    },
    onSuccess: ({ poId }) => {
      queryClient.invalidateQueries({ queryKey: ["po-line-items", poId] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      toast({ title: "Line removed", description: "Line item has been removed." });
    },
    onError: (error: Error) => {
      toast({ title: "Error removing line", description: error.message, variant: "destructive" });
    },
  });
}
